import React, { useState } from 'react';

import { Box } from '@mui/material';

import { CommonComponentProps, ImageObject } from '../pages/types';
import ImageEditorTooltip from './Editor/Components/ImageEditorTooltip';
import ImageFullscreen from './image-fullscreen';

interface SingleImageProps extends CommonComponentProps {
  image?: ImageObject;
  imageWidth?: string;
  imageHeight?: string;
  showCaption?: boolean;
}

const SingleImage: React.FC<SingleImageProps> = ({
  id,
  image,
  imageWidth = '100%',
  imageHeight = 'auto',
  showCaption = false,
  sx,
  isEditable,
  onPropsChange,
  onImageUpload,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!image || !image.url) {
    return null;
  }

  // Ensure URL is properly encoded
  const imageUrl = image.url.replace(/ /g, '%20');

  // Create an array with just this single image for the fullscreen viewer
  const images = [image];

  const handleImageClick = () => {
    if (isEditable && onImageUpload && onPropsChange) {
      onImageUpload((url) => {
        onPropsChange({
          image: {
            ...image,
            url: url,
          },
        });
      });
    }
  };

  const content = (
    <Box
      component="img"
      src={imageUrl}
      alt={image.altText || 'Image'}
      sx={{
        width: imageWidth ? imageWidth : '100%',
        height: imageHeight ? imageHeight : 'auto',
        cursor: isEditable ? 'pointer' : 'default',
        '&:hover': isEditable
          ? {
              opacity: 0.8,
              outline: '2px dashed #1976d2',
            }
          : {},
      }}
      // onClick={isEditable ? handleImageClick : undefined} // Disabled main click in editor
    />
  );

  return (
    <Box
      id={id}
      sx={{
        maxWidth: '1100px',
        width: '100%',
        textAlign: 'center',
        margin: '0 auto',
        ...sx,
      }}
    >
      {isEditable ? (
        <ImageEditorTooltip
          image={image}
          onEdit={() => {
            if (isEditable && onImageUpload && onPropsChange) {
              onImageUpload((url) => {
                onPropsChange({
                  image: {
                    ...image,
                    url: url,
                  },
                });
              });
            }
          }}
          onDelete={() => {
            if (onPropsChange) {
              onPropsChange({ image: undefined });
            }
          }}
          onTextEdit={(title, altText) => {
            if (onPropsChange) {
              onPropsChange({
                image: {
                  ...image,
                  title,
                  altText,
                },
              });
            }
          }}
        >
          {content}
        </ImageEditorTooltip>
      ) : (
        <ImageFullscreen
          firstImage={image}
          images={images}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
        >
          {content}
        </ImageFullscreen>
      )}
    </Box>
  );
};

export default SingleImage;
